import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useTranslation } from 'react-i18next';

import Seo from '../../components/SEO';
import { gearApiClient } from '../../gearApiClient';
import { gearIcons, getGearLabel } from '../../gears';
import type { GearRental, GearRentalStatus } from '../../models/GearRental';

type GearRentalActionType = 'confirm' | 'complete' | 'cancel';

function formatDate(value: string, language: string): string {
  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatAddress(rental: GearRental): string {
  return [rental.addressLine1, rental.addressLine2, rental.city, rental.countryCode].filter(Boolean).join(', ');
}

function statusColor(status: GearRentalStatus): 'warning' | 'info' | 'success' | 'default' {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'CONFIRMED':
      return 'info';
    case 'COMPLETED':
      return 'success';
    default:
      return 'default';
  }
}

function sortedRentals(rentals: GearRental[]): GearRental[] {
  return [...rentals].sort((left, right) => right.startDate.localeCompare(left.startDate));
}

function getActionDialogTitle(t: ReturnType<typeof useTranslation>['t'], actionType: GearRentalActionType | null): string {
  const titles: Record<GearRentalActionType, string> = {
    confirm: t('hostGearRentals.confirmDialogTitle', 'Confirm rental'),
    complete: t('hostGearRentals.completeDialogTitle', 'Complete rental'),
    cancel: t('hostGearRentals.cancelDialogTitle', 'Cancel rental'),
  };

  return actionType ? titles[actionType] : '';
}

function getActionDialogBody(t: ReturnType<typeof useTranslation>['t'], actionType: GearRentalActionType | null): string {
  const bodies: Record<GearRentalActionType, string> = {
    confirm: t('hostGearRentals.confirmDialogBody', 'Confirm this gear rental request?'),
    complete: t('hostGearRentals.completeDialogBody', 'Mark this gear rental as completed?'),
    cancel: t('hostGearRentals.cancelDialogBody', 'Cancel this gear rental request?'),
  };

  return actionType ? bodies[actionType] : '';
}

async function executeGearRentalAction(actionType: GearRentalActionType, rentalId: string): Promise<GearRentalStatus> {
  switch (actionType) {
    case 'confirm':
      await gearApiClient.confirmHostGearRental(rentalId);
      return 'CONFIRMED';
    case 'complete':
      await gearApiClient.completeHostGearRental(rentalId);
      return 'COMPLETED';
    case 'cancel':
      await gearApiClient.cancelHostGearRental(rentalId);
      return 'CANCELLED';
  }
}

export default function HostGearRentalsPage() {
  const { t, i18n } = useTranslation();
  const [rentals, setRentals] = React.useState<GearRental[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedRental, setSelectedRental] = React.useState<GearRental | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedRentalLoading, setSelectedRentalLoading] = React.useState(false);
  const [selectedRentalError, setSelectedRentalError] = React.useState('');
  const [actionDialogOpen, setActionDialogOpen] = React.useState(false);
  const [actionTarget, setActionTarget] = React.useState<GearRental | null>(null);
  const [actionType, setActionType] = React.useState<GearRentalActionType | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);

  const loadRentals = React.useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await gearApiClient.getHostGearRentals();
      setRentals(sortedRentals(response));
    } catch {
      setRentals([]);
      setError(t('hostGearRentals.loadError', 'Failed to load host gear rentals'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadRentals();
  }, [loadRentals]);

  const updateRentalInState = React.useCallback((rentalId: string, nextStatus: GearRentalStatus) => {
    setRentals((previous) => previous.map((rental) => (rental.id === rentalId ? { ...rental, status: nextStatus } : rental)));
    setSelectedRental((previous) => (previous?.id === rentalId ? { ...previous, status: nextStatus } : previous));
  }, []);

  const openDetails = React.useCallback(async (rental: GearRental) => {
    setDrawerOpen(true);
    setSelectedRental(rental);
    setSelectedRentalLoading(true);
    setSelectedRentalError('');

    try {
      const response = await gearApiClient.getHostGearRental(rental.id);
      setSelectedRental(response);
    } catch {
      setSelectedRentalError(t('hostGearRentals.detailLoadError', 'Could not load rental details'));
    } finally {
      setSelectedRentalLoading(false);
    }
  }, [t]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRental(null);
    setSelectedRentalError('');
    setSelectedRentalLoading(false);
  };

  const openActionDialog = (type: GearRentalActionType, rental: GearRental) => {
    setActionType(type);
    setActionTarget(rental);
    setActionDialogOpen(true);
  };

  const closeActionDialog = () => {
    if (actionLoading) {
      return;
    }
    setActionDialogOpen(false);
    setActionType(null);
    setActionTarget(null);
  };

  const handleActionConfirm = async () => {
    const targetRental = actionTarget;
    const selectedActionType = actionType;

    if (!selectedActionType || !targetRental) {
      return;
    }

    setActionLoading(true);
    try {
      const nextStatus = await executeGearRentalAction(selectedActionType, targetRental.id);
      updateRentalInState(targetRental.id, nextStatus);
      closeActionDialog();
    } catch {
      setError(t('hostGearRentals.actionError', 'Could not update the rental status'));
    } finally {
      setActionLoading(false);
    }
  };

  let content: React.ReactNode = null;
  if (loading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (rentals.length === 0) {
    content = (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {t('hostGearRentals.emptyTitle', 'No gear rentals yet')}
        </Typography>
        <Typography color="text.secondary">
          {t('hostGearRentals.emptyBody', 'Incoming reservations will appear here once users start booking gear.')}
        </Typography>
      </Paper>
    );
  } else {
    content = (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflowX: 'auto' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('hostGearRentals.columns.renter', 'Renter')}</TableCell>
                <TableCell>{t('hostGearRentals.columns.gear', 'Gear')}</TableCell>
                <TableCell>{t('hostGearRentals.columns.dates', 'Dates')}</TableCell>
                <TableCell>{t('hostGearRentals.columns.quantity', 'Quantity')}</TableCell>
                <TableCell>{t('hostGearRentals.columns.status', 'Status')}</TableCell>
                <TableCell align="right">{t('hostGearRentals.columns.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rentals.map((rental) => {
                const icon = gearIcons[rental.gearType] ?? <Inventory2Icon fontSize="small" />;
                const canConfirm = rental.status === 'PENDING';
                const canComplete = rental.status === 'CONFIRMED';
                const canCancel = rental.status === 'PENDING' || rental.status === 'CONFIRMED';

                return (
                  <TableRow
                    key={rental.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => void openDetails(rental)}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={700}>
                          {rental.renterName || t('hostGearRentals.unknownRenter', 'Unknown renter')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rental.renterPhone || '—'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '12px',
                            bgcolor: 'primary.lighter',
                            color: 'primary.main',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {getGearLabel(rental.gearType)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rental.organizationName}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(rental.startDate, i18n.language)} {'\u2013'} {formatDate(rental.endDate, i18n.language)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rental.quantity}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(rental.status)} label={t(`hostGearRentals.status.${rental.status}`, rental.status)} />
                    </TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        {canConfirm ? (
                          <Button size="small" variant="outlined" onClick={() => openActionDialog('confirm', rental)}>
                            {t('hostGearRentals.confirm', 'Confirm')}
                          </Button>
                        ) : null}
                        {canComplete ? (
                          <Button size="small" variant="outlined" color="success" onClick={() => openActionDialog('complete', rental)}>
                            {t('hostGearRentals.complete', 'Complete')}
                          </Button>
                        ) : null}
                        {canCancel ? (
                          <Button size="small" variant="outlined" color="error" onClick={() => openActionDialog('cancel', rental)}>
                            {t('hostGearRentals.cancel', 'Cancel')}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <>
      <Seo
        title={t('hostGearRentals.seoTitle', 'Gear rentals')}
        description={t('hostGearRentals.seoDescription', 'Review incoming gear reservations and manage their status.')}
      />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography component="h1" variant="h4" fontWeight={800} gutterBottom>
              {t('hostGearRentals.title', 'Gear rentals')}
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 820 }}>
              {t('hostGearRentals.subtitle', 'See every rental for your organization, open the detail panel, and act on pending requests.')}
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {content}
        </Stack>
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440, md: 520 },
            maxWidth: '100vw',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('hostGearRentals.detailOverline', 'Rental details')}
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {selectedRental ? getGearLabel(selectedRental.gearType) : t('hostGearRentals.detailFallbackTitle', 'Gear rental')}
              </Typography>
              {selectedRental ? (
                <Typography variant="body2" color="text.secondary">
                  {selectedRental.organizationName} · {selectedRental.renterName || t('hostGearRentals.unknownRenter', 'Unknown renter')}
                </Typography>
              ) : null}
            </Box>

            {selectedRentalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : null}

            {selectedRentalError ? <Alert severity="warning">{selectedRentalError}</Alert> : null}

            {selectedRental ? (
              <Stack spacing={2}>
                <Chip
                  size="small"
                  sx={{ width: 'fit-content' }}
                  color={statusColor(selectedRental.status)}
                  label={t(`hostGearRentals.status.${selectedRental.status}`, selectedRental.status)}
                />

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('hostGearRentals.renterInfo', 'Renter info')}
                    </Typography>
                    <Divider />
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.renterName', 'Renter name')}
                      </Typography>
                      <Typography>{selectedRental.renterName || '—'}</Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.renterPhone', 'Phone')}
                      </Typography>
                      <Typography>{selectedRental.renterPhone || '—'}</Typography>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('hostGearRentals.rentalInfo', 'Rental info')}
                    </Typography>
                    <Divider />
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.dates', 'Dates')}
                      </Typography>
                      <Typography>
                        {formatDate(selectedRental.startDate, i18n.language)} {'\u2013'} {formatDate(selectedRental.endDate, i18n.language)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.quantity', 'Quantity')}
                      </Typography>
                      <Typography>{selectedRental.quantity}</Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.pickupAddress', 'Pickup address')}
                      </Typography>
                      <Typography>{formatAddress(selectedRental)}</Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t('hostGearRentals.returnByNotice', 'Return by {{time}} on the return date.', { time: selectedRental.returnByTime ?? '12:00' })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Drawer>

      <Dialog open={actionDialogOpen} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getActionDialogTitle(t, actionType)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>{getActionDialogBody(t, actionType)}</Typography>
            {actionTarget ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={700}>
                    {actionTarget.renterName || t('hostGearRentals.unknownRenter', 'Unknown renter')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getGearLabel(actionTarget.gearType)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(actionTarget.startDate, i18n.language)} {'\u2013'} {formatDate(actionTarget.endDate, i18n.language)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => void handleActionConfirm()} variant="contained" disabled={actionLoading} color={actionType === 'cancel' ? 'error' : 'primary'}>
            {actionLoading ? t('common.loading', 'Loading...') : t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}